/**
 * Attaching the letter (docs/ux/intake.md). The LOI tab's empty state used to
 * say the letter is "attached to the case by the intake tool", which named a
 * tool the reader has no access to from the screen they are on. A case at 1
 * with no letter is the normal state after New case, so the tab says so and
 * offers the two ways forward.
 *
 * A demo file is copied server side rather than fetched and re-uploaded: the
 * bytes are real material and there is no reason for them to pass through the
 * browser at all.
 */

import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { reasonOf } from '@/app/api';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/shell/states';
import { useDemoPrefill, useLetterFromDemo } from './useIntake';

export function AttachLetter({ matterRef, onAttached }: { matterRef: string; onAttached: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prefill = useDemoPrefill();
  const fromDemo = useLetterFromDemo(matterRef);
  const demoFiles = prefill.data?.files.filter((f) => f.pdf) ?? [];

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/matters/${encodeURIComponent(matterRef)}/loi/files/letter`, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/pdf' },
        body: file,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(response.status === 415 ? 'The letter must be a PDF.' : 'The letter could not be stored.');
      toast.success('Letter attached. Run the extraction when you are ready.');
      onAttached();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The letter could not be stored.');
    } finally {
      setBusy(false);
    }
  }

  async function attachDemo(name: string) {
    setError(null);
    try {
      await fromDemo.mutateAsync(name);
      toast.success('Letter attached. Run the extraction when you are ready.');
      onAttached();
    } catch (err) {
      setError(reasonOf(err) ?? 'The demo letter could not be attached.');
    }
  }

  return (
    <EmptyState
      title={`No letter is on file for ${matterRef}.`}
      description="The extraction runs on the letter, so it is the first thing this case needs."
      action={
        <div className="flex flex-col items-center gap-2" data-slot="attach-letter">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => input.current?.click()} disabled={busy || fromDemo.isPending}>
              {busy ? 'Uploading' : 'Upload a letter'}
            </Button>
            {demoFiles.map((f) => (
              <Button key={f.name} variant="outline" disabled={busy || fromDemo.isPending} onClick={() => void attachDemo(f.name)}>
                Use {f.name}
              </Button>
            ))}
          </div>
          <input
            ref={input}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            aria-label="Letter of instruction, as a PDF"
            onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); e.target.value = ''; }}
          />
          {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}
        </div>
      }
    />
  );
}
